import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
} from '@mui/material';
import {
  ChipOption,
  ChipSelectList,
  OutlinedIcons,
  useApiClient,
  useFetch,
} from '@marageti/z4-lib';
import { useParams } from 'react-router-dom';
import { Doc, PreSignedURL } from '@marageti/z4-sdk/lib/documents';
import ZSpinner from '../../../components/z-lib/z-spinner';

const { OutlinedInformationCircleIcon } = OutlinedIcons;

const formatDateToUpdatedDate = (isoString: string): string => {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return 'Invalid date';

  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getDate();
  const year = date.getFullYear();
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');

  return `${month} ${day}, ${year} at ${hours}:${minutes} (UTC)`;
};

const reportChips: ChipOption[] = [
  { key: 0, label: 'Company | 30-210 Days', value: 'Sales Metrics by Company (30-210)' },
  { key: 1, label: 'Company | 30-90 Days', value: 'Sales Metrics by Company (30-90)' },
  { key: 2, label: 'Specialist | 30-90 Days', value: 'Sales Metrics by Specialist' },
];

const ReportNotFound = () => (
  <Box sx={{ textAlign: 'center', py: 8, backgroundColor: 'background.paper' }}>
    <OutlinedInformationCircleIcon
      sx={{
        color: 'common.black50',
        fontSize: '3rem',
        '& svg': {
          strokeWidth: '1px',
        },
        '& path': {
          strokeWidth: '1px',
        },
      }}
    />
    <Typography variant="body1" color="text.secondary">
      There are no reports available
    </Typography>
  </Box>
);

const ReportsContainer = () => {
  const { partnerId } = useParams();
  const apiClient = useApiClient();
  const { data: reports, isLoading: isReportsLoading } = useFetch<Doc[]>(apiClient.partnersClient.getPartnerReports, [partnerId], !!partnerId);
  const [selectedReport, setSelectedReport] = useState<string[]>(['Sales Metrics by Company (30-210)']);
  const reportToDownload = reports?.find((report) => report.title === selectedReport[0]) || {} as Doc;
  const { data: reportToDisplay, isLoading: isReportUrlLoading } = useFetch<PreSignedURL>(apiClient.documentsClient.getDocumentDownloadUrl, [reportToDownload.id], !!reportToDownload.id);
  // @ts-ignore
  const updatedDate = formatDateToUpdatedDate(reportToDownload.fileLastModifiedAt);
  const noReports = !reportToDownload.id && !isReportsLoading && !isReportUrlLoading;
  const isLoading = isReportsLoading || isReportUrlLoading;
  const reportUrl = reportToDisplay?.url || '';

  return (
    <Container sx={{ py: 3, mt: '48px' }}>
      <Box sx={{ mb: 3 }}>
        <ChipSelectList
          label="Report By"
          chipOptions={reportChips}
          selectedChips={selectedReport}
          onChange={
            (selectedChips) => setSelectedReport((state) => {
              if (selectedChips.length === 0) {
                return state;
              }
              return selectedChips.filter((chip) => !state.includes(chip));
            })
          }
        />
      </Box>
      <Box
        sx={{
          mb: 5.5,
          maxWidth: { desktop: 'desktop' },
          margin: { desktop: '0 auto 56px' },
        }}
      >
        {reportUrl?.length !== 0 && (
          <>
            <iframe
              key={reportToDownload.id}
              src={reportUrl}
              width="100%"
              height="1200"
              style={{ border: 'none', borderRadius: '12px' }}
              title="Sales Metrics Report"
            />
            <Typography variant="body1" color="text.secondary">
              Last updated on
              {` ${updatedDate}`}
            </Typography>
          </>
        )}
        {noReports && (
          <ReportNotFound />
        )}
        {isLoading && (
          <Box display="flex" justifyContent="center">
            <ZSpinner />
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default ReportsContainer;
